if (!customElements.get('variant-options-popover')) {
  customElements.define('variant-options-popover', class extends HTMLElement {
    connectedCallback() {
      if (this.hasAttribute('data-wired')) return;
      this.setAttribute('data-wired', '');

      const trigger = this.querySelector('[data-options-trigger]');
      const menu = this.querySelector('[data-options-popover]');
      if (!trigger || !menu) return;

      this._trigger = trigger;
      this._menu = menu;
      this._drawer = this.closest('cart-drawer');

      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggle();
      });

      menu.querySelectorAll('[data-variant-id]').forEach((opt) => {
        opt.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (opt.hasAttribute('disabled')) return;
          const form = this.closest('form');
          const input = form && form.querySelector('[data-variant-input]');
          if (!form || !input) return;

          input.value = opt.dataset.variantId;

          // Fire the card's loader so the user gets feedback while the cart updates
          const card = this.closest('line-item.mini-cart__product-card');
          if (card) {
            card.dispatchEvent(new CustomEvent('line-item:change:start', {
              bubbles: true,
              detail: { newLineQuantity: 1 },
            }));
          }

          // Submit FIRST so the form is in its expected DOM state, then close the popover
          if (typeof form.requestSubmit === 'function') {
            form.requestSubmit();
          } else {
            form.submit();
          }

          this.close();
        });
      });

      this._onDocClick = (e) => {
        if (this.contains(e.target)) return;
        if (menu.contains(e.target)) return;
        this.close();
      };

      this._onEsc = (e) => {
        if (e.key === 'Escape') this.close();
      };

      this._closeOnMove = (e) => {
        // Ignore scroll events originating inside the popover itself
        if (e && e.type === 'scroll' && this._menu && this._menu.contains(e.target)) return;
        this.close();
      };
    }

    disconnectedCallback() {
      this.detachGlobalListeners();
      // Clean up the portaled menu if it's still hanging on the drawer
      if (this._menu && this._drawer && this._menu.parentNode === this._drawer) {
        this._menu.remove();
      }
    }

    toggle() {
      this._menu.classList.contains('is-visible') ? this.close() : this.open();
    }

    open() {
      if (!this._drawer) return;

      // Close any other open popover first
      document.querySelectorAll('.mini-cart__variant-options__popover.is-visible').forEach((m) => {
        if (m !== this._menu) m.classList.remove('is-visible');
      });

      // Portal the menu up to cart-drawer (sibling of drawer__content / drawer__footer)
      // so it escapes drawer__content's overflow clipping.
      if (this._menu.parentNode !== this._drawer) {
        this._drawer.appendChild(this._menu);
      }

      this._menu.classList.add('is-visible');
      this.position();
      this._trigger.setAttribute('aria-expanded', 'true');

      // If popover would overflow the footer, scroll the drawer so it fits, then remember
      // the original scroll position so we can restore it on close.
      this._scrollContent = this._drawer.querySelector('.drawer__content');
      this._originalScrollTop = this._scrollContent ? this._scrollContent.scrollTop : null;
      this.scrollIntoRoom();

      document.addEventListener('click', this._onDocClick);
      document.addEventListener('keydown', this._onEsc);
      window.addEventListener('resize', this._closeOnMove);
      // Defer the scroll listener until the next frame so the programmatic scroll above
      // doesn't immediately re-close the popover.
      requestAnimationFrame(() => {
        if (this._menu.classList.contains('is-visible')) {
          window.addEventListener('scroll', this._closeOnMove, { passive: true, capture: true });
        }
      });
    }

    scrollIntoRoom() {
      if (!this._scrollContent) return;
      const triggerRect = this._trigger.getBoundingClientRect();
      const footer = this._drawer.querySelector('.mini-cart__drawer-footer, .drawer__footer');
      const footerTop = footer ? footer.getBoundingClientRect().top : window.innerHeight;
      const menuHeight = this._menu.offsetHeight || 220;
      const gap = 6;
      const pad = 8;
      const overflow = (triggerRect.bottom + gap + menuHeight) - (footerTop - pad);
      if (overflow > 0) {
        this._scrollContent.scrollTop += overflow;
        this.position();
      }
    }

    close() {
      if (!this._menu.classList.contains('is-visible')) return;
      this._menu.classList.remove('is-visible');
      this._trigger.setAttribute('aria-expanded', 'false');
      this.detachGlobalListeners();
      // Restore the drawer's original scroll position
      if (this._scrollContent && this._originalScrollTop !== null) {
        this._scrollContent.scrollTop = this._originalScrollTop;
      }
      this._scrollContent = null;
      this._originalScrollTop = null;
    }

    detachGlobalListeners() {
      if (this._onDocClick) document.removeEventListener('click', this._onDocClick);
      if (this._onEsc) document.removeEventListener('keydown', this._onEsc);
      if (this._closeOnMove) {
        window.removeEventListener('resize', this._closeOnMove);
        window.removeEventListener('scroll', this._closeOnMove, { capture: true });
      }
    }

    position() {
      if (!this._drawer || !this._trigger || !this._menu) return;
      const triggerRect = this._trigger.getBoundingClientRect();
      const drawerRect = this._drawer.getBoundingClientRect();
      // Always drop straight down from the trigger
      this._menu.style.top = `${triggerRect.bottom - drawerRect.top + 6}px`;
    }
  });
}
